import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, CheckCircle, ChevronRight, Flame, CalendarDays } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function formatDateLabel(dateStr) {
  try {
    const d = new Date(dateStr + "T12:00:00");
    const today = new Date().toISOString().slice(0, 10);
    if (dateStr === today) return "Aujourd'hui";
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return dateStr;
  }
}

function MetricPill({ label, value }) {
  if (value == null) return null;
  const low = value <= 2;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${low ? "bg-red-50 text-red-600 font-medium" : "bg-slate-100 text-slate-600"}`}>
      {label}: {value}/5
    </span>
  );
}

function StudentRowSkeleton() {
  return (
    <Card className="border-slate-100">
      <CardContent className="p-4 flex items-center gap-4">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function CoachDailyCheckins() {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = () => {
    setLoading(true);
    api.get(`/checkins/daily/coach/overview?date=${date}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchOverview(); }, [date]);

  const students = data?.students || [];

  return (
    <div data-testid="coach-daily-checkins-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">
            Check-ins quotidiens
          </h1>
          <p className="text-slate-500 mt-1">
            Suivi du bien-être de vos élèves — {formatDateLabel(date)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-auto h-10"
            max={new Date().toISOString().slice(0, 10)}
          />
        </div>
      </div>

      {!loading && data && (
        <div className="grid sm:grid-cols-3 gap-4 mt-6">
          <Card className="border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{data.checked_in}/{data.total}</p>
                <p className="text-xs text-slate-500">Check-ins complétés</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{data.alert_count}</p>
                <p className="text-xs text-slate-500">Alertes actives</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Flame className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{data.total - data.checked_in}</p>
                <p className="text-xs text-slate-500">En attente</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <StudentRowSkeleton key={i} />)
        ) : students.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400">Aucun élève assigné.</p>
          </div>
        ) : (
          students.map((s) => (
            <Card
              key={s.student_id}
              className={`border-slate-100 transition-colors hover:border-blue-100 ${s.has_alerts ? "border-l-4 border-l-orange-400" : ""}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  <Avatar className="h-10 w-10 flex-shrink-0">
                    <AvatarImage src={s.picture ? `${API_BASE}${s.picture}` : undefined} />
                    <AvatarFallback className="bg-blue-100 text-blue-600 text-sm font-semibold">
                      {s.student_name?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{s.student_name}</h3>
                        {s.checkin ? (
                          <Badge className="bg-green-50 text-green-700 border-green-100" variant="outline">Fait</Badge>
                        ) : (
                          <Badge className="bg-slate-50 text-slate-500" variant="outline">En attente</Badge>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-blue-600"
                        onClick={() => navigate(`/coach/students/${s.student_id}`)}
                      >
                        Voir profil <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                      </Button>
                    </div>

                    {s.checkin && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <MetricPill label="Humeur" value={s.checkin.mood} />
                        <MetricPill label="Énergie" value={s.checkin.energy} />
                        <MetricPill label="Sommeil" value={s.checkin.sleep_quality} />
                        <MetricPill label="Adhérence" value={s.checkin.adherence} />
                      </div>
                    )}

                    {s.checkin?.notes && (
                      <p className="text-xs text-slate-500 mt-2 italic line-clamp-2">"{s.checkin.notes}"</p>
                    )}

                    {s.alerts?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {s.alerts.map((a, i) => (
                          <Badge
                            key={i}
                            variant="outline"
                            className={a.severity === "alert"
                              ? "bg-red-50 text-red-600 border-red-100"
                              : "bg-orange-50 text-orange-600 border-orange-100"}
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {a.label}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}